/**
 * Portuguese Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const pt: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Painel',
  'nav.funds': 'Fundos',
  'nav.investments': 'Investimentos',
  'nav.investees': 'Investidas',
  'nav.esg': 'ESG Revisão',
  'nav.compliance': 'Conformidade',
  'nav.reports': 'Relatórios',
  'nav.settings': 'Definições',
  'nav.users': 'Utilizadores',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indicadores',
  'nav.taxonomy': 'Taxonomia da UE',
  'nav.filings': 'Declarações',
  'nav.calendar': 'Calendário',
  'nav.submit': 'Submeter dados',
  'nav.submissions': 'As minhas submissões',
  'nav.benchmarks': 'Referências',
  'nav.messages': 'Mensagens',
  'nav.portfolio': 'Carteira',
  'nav.performance': 'Desempenho dos fundos',
  'nav.esgSummary': 'ESG Resumo',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Métricas de impacto',
  'nav.sdg': 'Mapeamento ODS',
  'nav.audit': 'Trilha de auditoria',
  'nav.snapshots': 'Snapshots de dados',
  'nav.knowledgeGraph': 'Grafo de conhecimento',
  'nav.geographic': 'Geográfico',
  'nav.bi': 'BI Estúdio',
  'nav.ai': 'Assistente IA',
  'nav.tasks': 'Tarefas',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Monitor regulatório',

  // Common Actions
  'action.create': 'Criar',
  'action.edit': 'Editar',
  'action.delete': 'Eliminar',
  'action.save': 'Guardar',
  'action.cancel': 'Cancelar',
  'action.submit': 'Submeter',
  'action.approve': 'Aprovar',
  'action.reject': 'Rejeitar',
  'action.download': 'Descarregar',
  'action.export': 'Exportar',
  'action.import': 'Importar',
  'action.filter': 'Filtrar',
  'action.search': 'Pesquisar',
  'action.refresh': 'Atualizar',
  'action.close': 'Fechar',
  'action.back': 'Voltar',
  'action.next': 'Seguinte',
  'action.previous': 'Anterior',
  'action.viewAll': 'Ver tudo',
  'action.viewDetails': 'Ver detalhes',
  'action.generate': 'Gerar',
  'action.signOut': 'Terminar sessão',
  'action.addNew': 'Adicionar novo',
  'action.invite': 'Convidar',

  // Statuses
  'status.draft': 'Rascunho',
  'status.submitted': 'Submetido',
  'status.validated': 'Validado',
  'status.approved': 'Aprovado',
  'status.rejected': 'Rejeitado',
  'status.pending': 'Pendente',
  'status.inProgress': 'Em curso',
  'status.overdue': 'Em atraso',
  'status.compliant': 'Conforme',
  'status.filed': 'Declarado',
  'status.active': 'Ativo',
  'status.inactive': 'Inativo',
  'status.closed': 'Encerrado',
  'status.fundraising': 'Captação de recursos',
  'status.investing': 'Investimento',
  'status.harvesting': 'Colheita',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Visão geral da carteira',
  'dashboard.complianceStatus': 'Estado de conformidade',
  'dashboard.collectionProgress': 'Progresso da recolha',
  'dashboard.activityFeed': 'Atividade',
  'dashboard.quickActions': 'Ações rápidas',
  'dashboard.investmentPipeline': 'Pipeline de investimento',
  'dashboard.totalFunds': 'Total de fundos',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Investimentos ativos',
  'dashboard.totalInvestees': 'Investidas',
  'dashboard.recentActivity': 'Atividade recente',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Total comprometido',
  'lp.capitalCalled': 'Capital chamado',
  'lp.distributed': 'Distribuído',
  'lp.netPosition': 'Posição líquida',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Desempenho dos fundos',
  'lp.esgSummary': 'ESG Resumo',
  'lp.reportLibrary': 'Biblioteca de relatórios',
  'lp.taxonomyAligned': 'Alinhado com a taxonomia',
  'lp.paiCoverage': 'PAI Cobertura',
  'lp.portfolioPerformance': 'Desempenho da carteira LP',

  // Fund Labels
  'fund.name': 'Nome do fundo',
  'fund.code': 'Código do fundo',
  'fund.vintage': 'Ano de constituição',
  'fund.size': 'Dimensão do fundo',
  'fund.targetSize': 'Dimensão alvo',
  'fund.currency': 'Moeda',
  'fund.strategy': 'Estratégia',
  'fund.status': 'Estado',
  'fund.sfdrCategory': 'SFDR Categoria',
  'fund.geographyFocus': 'Foco geográfico',
  'fund.sectorFocus': 'Foco setorial',

  // Form Labels
  'form.name': 'Nome',
  'form.email': 'E-mail',
  'form.country': 'País',
  'form.amount': 'Montante',
  'form.currency': 'Moeda',
  'form.date': 'Data',
  'form.description': 'Descrição',
  'form.notes': 'Notas',
  'form.type': 'Tipo',
  'form.period': 'Período',
  'form.year': 'Ano',
  'form.required': 'Obrigatório',
  'form.optional': 'Opcional',

  // Table Headers
  'table.fund': 'Fundo',
  'table.commitment': 'Compromisso',
  'table.called': 'Chamado',
  'table.distributed': 'Distribuído',
  'table.tvpi': 'TVPI',
  'table.status': 'Estado',
  'table.regulation': 'Regulamento',
  'table.filingType': 'Tipo de declaração',
  'table.reportingPeriod': 'Período',
  'table.createdAt': 'Criado',
  'table.actions': 'Ações',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taxonomia da UE',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Completude dos dados',
  'compliance.nextDeadline': 'Próximo prazo',

  // Reports
  'report.quarterly': 'Relatório trimestral',
  'report.annual': 'Relatório anual',
  'report.paiStatement': 'PAI Declaração',
  'report.taxonomyReport': 'Relatório de taxonomia',
  'report.generatedAt': 'Gerado',
  'report.version': 'Versão',

  // General
  'general.loading': 'A carregar...',
  'general.noData': 'Sem dados disponíveis',
  'general.error': 'Ocorreu um erro',
  'general.retry': 'Tentar novamente',
  'general.total': 'Total',
  'general.page': 'Página',
  'general.of': 'de',
  'general.showing': 'A mostrar',
  'general.results': 'resultados',
  'general.language': 'Idioma',
  'general.english': 'Inglês',
  'general.french': 'Francês',
};
